// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {get, isEqual, isPlainObject} from 'lodash';
import xml2js from 'xml2js';

// Core Libs
import {parseJson} from '../../lib/core/object';

// Components
import Error from './error';
import Pagination from './pagination';
import PanelAction from './panel-action';
import RecordDetails from './record-details';
import {Ide} from './input-ide';

// Styles
import './data-viewer.scss';

class DataViewer extends Component {
    state = {
        currentPage: 0,
        showDetail: !this.props.showEditor, // eslint-disable-line react/destructuring-assignment
    };

    componentDidUpdate(nextProps) {
        const {searchData} = this.props;
        const {currentPage} = this.state;

        if (currentPage && !isEqual(searchData, nextProps.searchData)) {
            this.setState({currentPage: 0});
        }
    }

    getAceProps() {
        const {mode, readOnly} = this.props;

        return {
            className: 'mediaitem-json--ide',
            mode,
            readOnly,
        };
    }

    getClass() {
        const {className} = this.props;

        return classnames('data-viewer', className);
    }

    getDataRecord() {
        const {searchData} = this.props;
        const {currentPage} = this.state;
        return get(searchData, currentPage);
    }

    getPanelActionProps() {
        return {
            onClick: this.toggleView,
            hotkey: 'j',
            icon: 'code',
            title: 'View JSON',
            weight: 91,
        };
    }

    handlePaginationClick = pageNumber => {
        this.setState({currentPage: pageNumber - 1});
    };

    toggleView = () => {
        this.setState(prevState => ({
            showDetail: !prevState.showDetail,
        }));
    };

    renderDetail() {
        const {defaultExpandKeys, mode} = this.props;
        const dataRecord = this.getDataRecord();
        let formattedJson;
        let validJson;

        if (dataRecord) {
            if (mode === 'json') {
                if (isPlainObject(dataRecord)) {
                    formattedJson = dataRecord;
                    validJson = true;
                } else {
                    formattedJson = parseJson(dataRecord);
                    validJson = formattedJson !== undefined;
                }
            } else if (mode === 'xml') {
                xml2js.parseString(
                    dataRecord,
                    {
                        mergeAttrs: true,
                        explicitArray: false,
                    },
                    (_err, result) => {
                        formattedJson = result;
                        validJson = true;
                    }
                );
            }
        }

        return dataRecord ? (
            <div className="data-viewer--record">
                {!validJson ? <Error error="Unable to parse data" /> : null}
                <RecordDetails
                    className="data-viewer--record-details"
                    defaultExpandKeys={defaultExpandKeys}
                    searchData={[formattedJson]}
                />
            </div>
        ) : null;
    }

    renderEditor() {
        const {mode, readOnly} = this.props;
        const dataRecord = this.getDataRecord();
        const ideProps = {
            className: 'data-viewer--ide',
            mode,
            readOnly,
        };
        let formattedJson;
        let stringifiedData;
        let validJson;

        if (mode === 'json' && dataRecord) {
            if (isPlainObject(dataRecord)) {
                formattedJson = dataRecord;
                stringifiedData = JSON.stringify(formattedJson, null, '\t');
                validJson = true;
            } else {
                formattedJson = parseJson(dataRecord);
                validJson = formattedJson !== undefined;
                stringifiedData = validJson ? JSON.stringify(formattedJson, null, '\t') : dataRecord;
            }
        } else if (mode === 'xml' && dataRecord) {
            formattedJson = {test: 1};
            stringifiedData = dataRecord;
            validJson = true;
        }

        return dataRecord ? (
            <div className="data-viewer--record">
                {!validJson ? <Error error="Unable to parse data" /> : null}
                <Ide {...ideProps} value={stringifiedData} />
            </div>
        ) : null;
    }

    renderPagination() {
        const {searchData} = this.props;
        const {currentPage} = this.state;
        const totalPages = searchData.length;
        const props = {
            children: 'Record:',
            className: 'data-viewer--pagination',
            currentPage: currentPage + 1,
            onClick: this.handlePaginationClick,
            totalPages,
        };

        return totalPages > 1 ? <Pagination {...props} /> : null;
    }

    render() {
        const {showDetail} = this.state;

        return (
            <div className={this.getClass()}>
                {this.renderPagination()}
                <PanelAction {...this.getPanelActionProps()} />
                {showDetail ? this.renderDetail() : this.renderEditor()}
            </div>
        );
    }
}

DataViewer.propTypes = {
    aceProps: PropTypes.object,
    className: PropTypes.string,
    defaultExpandKeys: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.bool]),
    mode: PropTypes.string,
    readOnly: PropTypes.bool,
    searchData: PropTypes.array,
    showEditor: PropTypes.bool,
    value: PropTypes.string,
};

DataViewer.defaultProps = {
    mode: 'json',
    readOnly: true,
};

export default DataViewer;
